import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import { AssemblyGroup, ExternalWebhook, Station } from '@paragon/contract-library';
import { userService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { sortBy } from '@paragon/utilities';

import { createScrollbarStyling } from '../createScrollbarStyling';

const exampleAssemblyGroup: AssemblyGroup = {
  guid: '7f57f94d-ef8e-4d24-8afe-dfd023a76ac2',
  name: 'Deerfield Floors 2B',
  organization: 'Test Organization',
  created: '2026-01-10T01:23:15.301657+00:00',
  componentDesigns: {
    '5a8bf017-13a1-41f5-a807-efd6a6d4f701': {
      index: null,
      quantity: 1,
    },
    'a0e698ea-9856-4e55-96f4-dd6a92f98389': {
      index: null,
      quantity: 1,
    },
    'bb0d163b-2311-426a-b74c-e965d27fce6f': {
      index: null,
      quantity: 1,
    },
  },
  notes: null,
  archived: false,
};

const exampleStation: Station = {
  guid: 'fe5de7d9-0c2d-4ee6-8be2-a66b796dcd7f',
  organization: 'Test Organization',
  name: 'Bundler',
  followsSchedule: false,
  role: 'Assembly',
  currentProductionComponentInstanceGuid: null,
  componentDesigns: {
    '5a8bf017-13a1-41f5-a807-efd6a6d4f701': {
      index: null,
      quantity: null,
    },
    'a0e698ea-9856-4e55-96f4-dd6a92f98389': {
      index: null,
      quantity: null,
    },
    'bb0d163b-2311-426a-b74c-e965d27fce6f': {
      index: null,
      quantity: null,
    },
  },
};

const webhookExampleBodies: Record<string, any> = {
  'project.edited': {
    guid: '8aba9d93-d688-42f4-9280-847141c0be5c',
    event: 'project.edited',
    eventTimestamp: new Date().toISOString(),
    data: {
      guid: '0dce6f5b-c90d-4831-9621-7f61c5a174a8',
      name: 'Sample Project',
      componentDesignGuids: [
        '26c5a525-79b5-436a-a712-9b777b52260e',
        '8614af8c-daec-4d2c-b6f9-2657fa188c42',
        'f526c297-03a4-4c47-aa41-4579a1b0e893',
      ],
    },
  },
  'assemblyGroup.sentToStation': {
    guid: 'd4f5e6a7-b8c9-40da-9e0f-1a2b3c4d5e6f',
    event: 'assemblyGroup.sentToStation',
    eventTimestamp: new Date().toISOString(),
    data: {
      assemblyGroup: exampleAssemblyGroup,
      station: exampleStation,
    },
  },
};

const DATE_FORMATTING_OPTIONS = { month: 'long', day: 'numeric', year: 'numeric' } as const;

type OperationState = null | { type: 'renaming' | 'deleting'; guid: string } | { type: 'creating' };

// Implemented serverside in servers\user-service\src\controllers\externalWebhooks.js
function isValidUrl(urlString: string): { valid: true } | { valid: false; reason: string } {
  try {
    const url = new URL(urlString);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return { valid: false, reason: 'Invalid protocol' };
    if (isIpAddress(url.host)) return { valid: false, reason: 'IP addresses are not allowed' };
    if (url.host.includes('localhost')) return { valid: false, reason: 'Localhost is not allowed' };
    if (url.host.includes('paragontruss.com')) return { valid: false, reason: 'Paragon domains are not allowed' };
    return { valid: true };
  } catch (error) {
    return { valid: false, reason: 'Invalid URL' };
  }
}

function isIpAddress(host: string): boolean {
  // Regex for IPv4
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  // Regex for IPv6 (simplified for common cases)
  const ipv6Regex =
    /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::([0-9a-fA-F]{1,4}:){0,5}[0-9a-fA-F]{1,4}$|^[0-9a-fA-F]{1,4}::([0-9a-fA-F]{1,4}:){0,4}[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){0,5}::[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){0,6}::$/;

  return ipv4Regex.test(host) || ipv6Regex.test(host);
}

function CreateDialog(props: { onCancel: () => void; onSuccess: () => void; onFatalError: (error: any) => void }) {
  const [isValidating, setIsValidating] = useState(false);
  const [isWorking, setIsWorking] = useState(false);

  const [name, setName] = useState('');
  const [externalWebhookUrl, setExternalWebhookUrl] = useState('');
  const [secretToken, setSecretToken] = useState('');

  const externalUrlValidationState = isValidUrl(externalWebhookUrl.trim());
  const externalUrlErrorNote = isValidating
    ? externalUrlValidationState.valid
      ? null
      : externalUrlValidationState.reason
    : null;

  const externalWebhookInputRef = useRef<HTMLInputElement>(null);

  function onSubmit() {
    if (!isValidUrl(externalWebhookUrl.trim()).valid) {
      setIsValidating(true);
      externalWebhookInputRef.current!.focus();
      return;
    }

    setIsWorking(true);
    userService.externalWebhooks
      .createExternalWebhook({
        externalWebhookUrl: externalWebhookUrl.trim(),
        userProvidedName: name === '' ? null : name,
        secretToken: secretToken === '' ? null : secretToken,
      })
      .then(props.onSuccess)
      .catch(props.onFatalError)
      .finally(() => {
        setIsWorking(false);
      });
  }

  return (
    <Dialog open>
      <DialogTitle>Your New Webhook</DialogTitle>

      <DialogContent>
        <PropertyTextInput fullWidth property="Name" value={name} submit={setName} />
        <TextField
          fullWidth
          inputRef={externalWebhookInputRef}
          size="small"
          margin="dense"
          autoComplete="off"
          autoFocus
          required
          error={isValidating && !isValidUrl(externalWebhookUrl.trim()).valid}
          label="Webhook URL"
          value={externalWebhookUrl}
          onChange={(event) => {
            setExternalWebhookUrl(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSubmit();
            }
          }}
        />
        {externalUrlErrorNote != null && <Alert severity="error">{externalUrlErrorNote}</Alert>}
        <PropertyTextInput
          fullWidth
          property="Secret used to verify payload with SHA-256"
          value={secretToken}
          submit={setSecretToken}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={props.onCancel} disabled={isWorking}>
          Cancel
        </Button>

        <Button variant="outlined" disabled={externalWebhookUrl === '' || isWorking} onClick={onSubmit}>
          {isWorking ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RenameDialog(props: {
  externalWebhook: ExternalWebhook;
  onCancel: () => void;
  onSuccess: () => void;
  onFatalError: (error: any) => void;
}) {
  const [isValidating, setIsValidating] = useState(false);
  const [isWorking, setIsWorking] = useState(false);

  const [name, setName] = useState(props.externalWebhook.userProvidedName ?? '');

  const textFieldRef = useRef<HTMLInputElement>(null);

  function onSubmit() {
    if (name.trim() === '') {
      setIsValidating(true);
      textFieldRef.current!.focus();
      return;
    }

    setIsWorking(true);

    userService.externalWebhooks
      .renameExternalWebhook(props.externalWebhook.guid, name)
      .then(props.onSuccess)
      .catch(props.onFatalError);
  }

  return (
    <Dialog open>
      <DialogTitle>Rename Webhook</DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          inputRef={textFieldRef}
          size="small"
          margin="dense"
          autoComplete="off"
          autoFocus
          required
          error={isValidating && name.trim() === ''}
          label="Webhook Name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSubmit();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={props.onCancel} disabled={isWorking}>
          Cancel
        </Button>

        <Button variant="contained" disabled={isWorking} onClick={onSubmit}>
          {isWorking ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteConfirmationDialog(props: {
  externalWebhook: ExternalWebhook;
  onCancel: () => void;
  onSuccess: () => void;
  onFatalError: (error: any) => void;
}) {
  const [isWorking, setIsWorking] = useState(false);

  return (
    <Dialog open>
      <DialogTitle>Confirm deleting {props.externalWebhook.userProvidedName ?? 'unnamed webhook'}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Any applications using this webhook will stop working. Deleting a webhook cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isWorking} onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isWorking}
          color="error"
          onClick={() => {
            setIsWorking(true);
            userService.externalWebhooks
              .deleteExternalWebhook(props.externalWebhook.guid)
              .then(props.onSuccess)
              .catch(props.onFatalError);
          }}
        >
          I understand, delete this webhook
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ExternalWebhooksDialog(props: { onClose: () => void }) {
  const dispatch = useDispatch();

  const [selectedWebhookType, setSelectedWebhookType] = useState<string>('project.edited');
  const [operation, setOperation] = useState<OperationState>(null);

  function concludeOperation() {
    setOperation(null);
  }

  function onRenameRequested(guid: string) {
    setOperation({ type: 'renaming', guid });
  }

  function onDeleteRequested(guid: string) {
    setOperation({ type: 'deleting', guid });
  }

  const [externalWebhooks, setExternalWebhooks] = useState<readonly ExternalWebhook[]>([]);

  function loadExternalWebhooks() {
    return userService.externalWebhooks.getExternalWebhooks().then(setExternalWebhooks);
  }

  const reloadExternalWebhooks = loadExternalWebhooks;

  useEffect(() => void loadExternalWebhooks(), []);

  const handleFatalErrorWhile = (operation: string) => (error: any) => {
    console.error(error);
    dispatch(enqueueError(`Error ${operation}. Please contact Paragon Customer Support.`));
    props.onClose();
  };

  return (
    <>
      {operation?.type === 'creating' && (
        <CreateDialog
          onCancel={concludeOperation}
          onSuccess={() => {
            reloadExternalWebhooks().then(concludeOperation).catch(handleFatalErrorWhile('creating external webhook'));
          }}
          onFatalError={handleFatalErrorWhile('creating external webhook')}
        />
      )}

      {operation?.type === 'deleting' && (
        <DeleteConfirmationDialog
          externalWebhook={externalWebhooks.find((webhook) => webhook.guid === operation.guid)!}
          onCancel={concludeOperation}
          onSuccess={() => {
            reloadExternalWebhooks().then(concludeOperation).catch(handleFatalErrorWhile('deleting external webhook'));
          }}
          onFatalError={handleFatalErrorWhile('deleting external webhook')}
        />
      )}

      {operation?.type === 'renaming' && (
        <RenameDialog
          externalWebhook={externalWebhooks.find((externalWebhook) => externalWebhook.guid === operation.guid)!}
          onCancel={concludeOperation}
          onSuccess={() => {
            reloadExternalWebhooks().then(concludeOperation).catch(handleFatalErrorWhile('renaming external webhook'));
          }}
          onFatalError={handleFatalErrorWhile('renaming external webhook')}
        />
      )}

      <Dialog open onClose={props.onClose}>
        <DialogTitle>Your Webhooks</DialogTitle>

        <DialogContent>
          <PropertySelectField
            options={Object.keys(webhookExampleBodies)}
            value={selectedWebhookType}
            submit={setSelectedWebhookType}
            property="Webhook Event Type"
          />
          <Typography component="pre">
            Example body:
            <br />
            <code>{JSON.stringify(webhookExampleBodies[selectedWebhookType], null, 2)}</code>
          </Typography>

          <Divider sx={{ marginY: 1 }} />

          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            loadingPosition="start"
            onClick={() => void setOperation({ type: 'creating' })}
          >
            Create New Webhook
          </Button>

          <Box sx={[{ overflowY: 'auto', maxHeight: '50vh' }, createScrollbarStyling]}>
            {externalWebhooks.length === 0 ? (
              <Paper variant="outlined" sx={{ marginTop: 1 }}>
                <Typography variant="body1" color="textSecondary" sx={{ margin: 2 }}>
                  No webhooks created yet.
                </Typography>
              </Paper>
            ) : (
              sortBy(
                externalWebhooks,
                [
                  (externalWebhook) => new Date(externalWebhook.createdAt),
                  (externalWebhook) => externalWebhook.userProvidedName ?? '',
                ],
                ['desc', 'desc'],
              ).map((externalWebhook) => (
                <Card key={externalWebhook.guid} sx={{ margin: 1 }}>
                  <>
                    <CardContent>
                      <Typography variant="h5">{externalWebhook.userProvidedName ?? 'unnamed webhook'}</Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Created:{' '}
                        {new Date(externalWebhook.createdAt).toLocaleDateString('en-US', DATE_FORMATTING_OPTIONS)}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        disabled={operation != null}
                        onClick={() => onRenameRequested(externalWebhook.guid)}
                      >
                        {externalWebhook.userProvidedName == null ? 'Name' : 'Rename'}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={operation != null}
                        onClick={() => onDeleteRequested(externalWebhook.guid)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </>
                </Card>
              ))
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={props.onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
